import React, { useMemo, useState } from "react";
import { Droplets, Layers, Sun, Wrench, ArrowRight, Info, Plus, Check } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { solarPackages, roofColors } from "../mock";
import { SolarCheckout } from "./SolarCheckout";
import { TaktvattBooking } from "./TaktvattBooking";
import { CAMPAIGN_DISCOUNT_PERCENT, CAMPAIGN_LABEL, campaignPrice } from "../lib/promotion";

// Area-based services use real client pricing.
const SERVICES = [
  { id: "taktvatt", label: "Taktvätt", icon: Droplets, type: "area", afterRot: true, min: 14900, perLo: 124, perHi: 165 },
  { id: "kombi", label: "Taktvätt + Takmålning", icon: Layers, type: "area", afterRot: true, min: 26600, max: 79800, perLo: 221, perHi: 290 },
  { id: "solcellstvatt", label: "Solcellstvätt", icon: Sun, type: "package" },
  { id: "nocktatning", label: "Nocktätning", icon: Wrench, type: "meter", perLo: 300, perHi: 450 },
];

// Nocktätning add-on pricing (after ROT, per running meter).
const NOCK = { perLo: 300, perHi: 450 };

const CONDITIONS = [
  { id: "latt", label: "Upp till 27°", sub: "19–27° lutning", mult: 1 },
  { id: "normal", label: "Upp till 37°", sub: "normal lutning", mult: 1.1 },
  { id: "kraftig", label: "Upp till 45°", sub: "brant tak", mult: 1.25 },
  { id: "brutet", label: "Brutet tak", sub: "mansardtak", mult: 1.5 },
];

const round100 = (n) => Math.round(n / 100) * 100;
const fmt = (n) => n.toLocaleString("sv-SE");
const parseKr = (s) => parseInt(String(s).replace(/\D/g, ""), 10) || 0;

export default function PriceCalculator() {
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get("granne");
  const [serviceId, setServiceId] = useState("taktvatt");
  const [area, setArea] = useState([120]);
  const [meters, setMeters] = useState([20]);
  const [cond, setCond] = useState("normal");
  const [pkg, setPkg] = useState(1); // index into solarPackages

  // Add-ons (only shown for area-based base services)
  const [addNock, setAddNock] = useState(false);
  const [nockMeters, setNockMeters] = useState([12]);
  const [addSolar, setAddSolar] = useState(false);
  const [solarPkg, setSolarPkg] = useState(1);
  const [roofColor, setRoofColor] = useState(null); // only for kombi (takmålning)

  const isKombi = serviceId === "kombi";
  const selectedColor = roofColors.find((c) => c.id === roofColor) || null;

  const svc = SERVICES.find((s) => s.id === serviceId);
  const isSolar = svc.type === "package";
  const isMeter = svc.type === "meter";
  const isArea = svc.type === "area";

  const result = useMemo(() => {
    if (isSolar) {
      const p = solarPackages[pkg];
      const basePrice = parseKr(p.price);
      return { solar: true, price: campaignPrice(basePrice), originalPrice: basePrice, label: p.panels };
    }
    if (isMeter) {
      const baseLow = round100(meters[0] * svc.perLo);
      const baseHigh = round100(meters[0] * svc.perHi);
      return { solar: false, meter: true, low: round100(campaignPrice(baseLow)), high: round100(campaignPrice(baseHigh)), originalLow: baseLow, originalHigh: baseHigh };
    }
    const c = CONDITIONS.find((x) => x.id === cond).mult;
    let lo = Math.max(svc.min, area[0] * svc.perLo * c);
    let hi = Math.max(svc.min * 1.05, area[0] * svc.perHi * c);
    if (svc.max) { lo = Math.min(lo, svc.max); hi = Math.min(hi, svc.max); }
    const baseLow = round100(lo);
    const baseHigh = round100(hi);
    return { solar: false, afterRot: !!svc.afterRot, low: round100(campaignPrice(baseLow)), high: round100(campaignPrice(baseHigh)), originalLow: baseLow, originalHigh: baseHigh, rot: round100(campaignPrice(baseLow * 0.7)) };
  }, [isSolar, isMeter, svc, area, meters, cond, pkg]);

  // Combined breakdown for area-based base + optional add-ons
  const breakdown = useMemo(() => {
    if (!isArea) return null;
    const lines = [{ key: "base", label: svc.label, lo: result.low, hi: result.high, unit: "kr" }];
    let totalLo = result.low;
    let totalHi = result.high;
    if (addNock) {
      const m = nockMeters[0];
      const lo = round100(campaignPrice(round100(m * NOCK.perLo)));
      const hi = round100(campaignPrice(round100(m * NOCK.perHi)));
      lines.push({ key: "nock", label: `Nocktätning (${m} m)`, lo, hi, unit: "kr" });
      totalLo += lo; totalHi += hi;
    }
    let solarYearly = 0;
    if (addSolar) {
      solarYearly = campaignPrice(parseKr(solarPackages[solarPkg].price));
      lines.push({ key: "solar", label: `Solcellstvätt (${solarPackages[solarPkg].panels})`, lo: solarYearly, hi: solarYearly, unit: "kr/år", yearly: true });
    }
    const hasAddons = addNock || addSolar;
    return { lines, totalLo, totalHi, solarYearly, hasAddons };
  }, [isArea, svc, result, addNock, nockMeters, addSolar, solarPkg]);

  const bookingProps = useMemo(() => {
    if (isSolar) return null;
    if (isMeter) {
      return {
        serviceLabel: svc.label,
        dayLong: false,
        priceText: `${fmt(result.low)}–${fmt(result.high)} kr`,
        details: `${meters[0]} löpmeter`,
      };
    }
    const condLabel = CONDITIONS.find((x) => x.id === cond)?.label;
    const parts = [`Takyta ${area[0]} m²`, `lutning ${condLabel}`];
    if (isKombi && selectedColor) parts.push(`kulör ${selectedColor.label}`);
    if (addNock) parts.push(`+ Nocktätning ${nockMeters[0]} m`);
    if (addSolar) parts.push(`+ Solcellstvätt (${solarPackages[solarPkg].panels})`);
    const pt = breakdown && breakdown.hasAddons
      ? `${fmt(breakdown.totalLo)}–${fmt(breakdown.totalHi)} kr`
      : `${fmt(result.low)}–${fmt(result.high)} kr`;
    return { serviceLabel: svc.label, dayLong: true, priceText: pt, details: parts.join(", ") };
  }, [isSolar, isMeter, svc, result, meters, cond, area, addNock, nockMeters, addSolar, solarPkg, breakdown, isKombi, selectedColor]);

  return (
    <section id="kalkylator" className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="reveal text-center max-w-2xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#1B45C4]">Priskalkylator</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900">Räkna ut ditt takpris på 30 sekunder</h2>
          <p className="mt-4 text-slate-600 text-lg">Välj tjänst så får du en ungefärlig prisbild direkt – <strong>{CAMPAIGN_DISCOUNT_PERCENT}% kampanjrabatt är redan avdragen</strong>.</p>
        </div>

        <div className="reveal mt-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-6 rounded-3xl ring-1 ring-slate-100 shadow-xl shadow-[#1B45C4]/5 overflow-hidden">
          {/* controls */}
          <div className="p-7 sm:p-10 bg-white light-card">
            <label className="text-sm font-bold text-slate-900">1. Välj tjänst</label>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {SERVICES.map((o) => {
                const Icon = o.icon;
                const active = serviceId === o.id;
                return (
                  <button key={o.id} onClick={() => setServiceId(o.id)} data-testid={`calc-service-${o.id}`}
                    className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${active ? "border-[#1B45C4] bg-[#38BDF8]/10" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-[#1B45C4] text-white" : "bg-slate-100 text-slate-500"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`font-semibold text-sm ${active ? "text-[#1B45C4]" : "text-slate-700"}`}>{o.label}</span>
                  </button>
                );
              })}
            </div>

            {isSolar ? (
              <div className="mt-8">
                <label className="text-sm font-bold text-slate-900">2. Antal paneler</label>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {solarPackages.map((p, i) => {
                    const active = pkg === i;
                    return (
                      <button key={p.name} onClick={() => setPkg(i)}
                        className={`flex items-center justify-between rounded-xl border-2 p-4 text-left transition-all ${active ? "border-[#1B45C4] bg-[#38BDF8]/10" : "border-slate-200 hover:border-slate-300"}`}>
                        <div>
                          <p className={`font-bold text-sm ${active ? "text-[#1B45C4]" : "text-slate-800"}`}>{p.name}</p>
                          <p className="text-xs text-slate-500">{p.panels}</p>
                        </div>
                        <span className={`font-extrabold ${active ? "text-[#1B45C4]" : "text-slate-700"}`}>{p.price}</span>
                      </button>
                    );
                  })}
                </div>
                <a href="/solcellstvatt" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B45C4] hover:underline">
                  Se allt om solcellstvätt <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <>
                <div className="mt-8 flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-900">{isMeter ? "2. Antal löpmeter (nock)" : "2. Takyta"}</label>
                  <span className="rounded-full bg-[#1B45C4]/10 px-3 py-1 text-sm font-bold text-[#1B45C4]">{isMeter ? `${meters[0]} m` : `${area[0]} m²`}</span>
                </div>
                {isMeter ? (
                  <>
                    <Slider value={meters} onValueChange={setMeters} min={4} max={60} step={1} className="mt-5" />
                    <div className="mt-2 flex justify-between text-xs text-slate-400"><span>4 m</span><span>60 m</span></div>
                  </>
                ) : (
                  <>
                    <Slider value={area} onValueChange={setArea} min={20} max={300} step={5} className="mt-5" />
                    <div className="mt-2 flex justify-between text-xs text-slate-400"><span>20 m²</span><span>300 m²</span></div>

                    <label className="mt-8 block text-sm font-bold text-slate-900">3. Takets lutning</label>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {CONDITIONS.map((c) => (
                        <button key={c.id} onClick={() => setCond(c.id)} data-testid={`calc-pitch-${c.id}`}
                          className={`rounded-xl border-2 py-3 px-3 text-left transition-all ${cond === c.id ? "border-[#1B45C4] bg-[#38BDF8]/10" : "border-slate-200 hover:border-slate-300"}`}>
                          <span className={`block text-sm font-bold ${cond === c.id ? "text-[#1B45C4]" : "text-slate-700"}`}>{c.label}</span>
                          <span className="block text-xs text-slate-500">{c.sub}</span>
                        </button>
                      ))}
                    </div>

                    {/* Takfärgval – endast för Taktvätt + Takmålning */}
                    {isKombi && (
                      <div className="mt-8" data-testid="calc-color-section">
                        <label className="text-sm font-bold text-slate-900">4. Välj takfärg</label>
                        <p className="mt-1 text-xs text-slate-500">Välj önskad kulör (Benders betongpanne-stil) – valfritt, exakt kulör bestäms vid besiktning.</p>
                        <div className="mt-3 grid grid-cols-4 gap-2.5">
                          {roofColors.map((c) => {
                            const active = roofColor === c.id;
                            return (
                              <button key={c.id} onClick={() => setRoofColor(active ? null : c.id)} data-testid={`calc-color-${c.id}`}
                                className={`group flex flex-col items-center gap-1.5 rounded-xl border-2 p-2 transition-all ${active ? "border-[#1B45C4] bg-[#38BDF8]/10" : "border-slate-200 hover:border-slate-300"}`}>
                                <span className="relative flex h-11 w-full items-center justify-center rounded-lg ring-1 ring-black/5" style={{ backgroundColor: c.hex }}>
                                  {active && <Check className="w-5 h-5 text-white drop-shadow" />}
                                </span>
                                <span className={`text-[11px] font-semibold leading-tight text-center ${active ? "text-[#1B45C4]" : "text-slate-600"}`}>{c.label}</span>
                              </button>
                            );
                          })}
                        </div>
                        <a href="/takmalning" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B45C4] hover:underline" data-testid="calc-color-ai-link">
                          Se färgen på ditt eget hus (AI) <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {/* Tillval (add-ons) */}
                    <div className="mt-8">
                      <label className="text-sm font-bold text-slate-900">{isKombi ? "5" : "4"}. Lägg till extra tjänster</label>
                      <p className="mt-1 text-xs text-slate-500">Valfritt – lägg till ovanpå din {svc.label.toLowerCase()} för ett samlat pris.</p>

                      {/* Nocktätning add-on */}
                      <div className={`mt-3 rounded-xl border-2 transition-all ${addNock ? "border-[#1B45C4] bg-[#38BDF8]/10" : "border-slate-200"}`}>
                        <button onClick={() => setAddNock((v) => !v)} data-testid="calc-addon-nock-toggle"
                          className="flex w-full items-center gap-3 p-4 text-left">
                          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${addNock ? "border-[#1B45C4] bg-[#1B45C4] text-white" : "border-slate-300 text-transparent"}`}>
                            {addNock ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 text-slate-400" />}
                          </span>
                          <span className="flex-1">
                            <span className={`block text-sm font-bold ${addNock ? "text-[#1B45C4]" : "text-slate-700"}`}>Nocktätning</span>
                            <span className="block text-xs text-slate-500">Tätar nock & valv – {NOCK.perLo}–{NOCK.perHi} kr/m efter ROT</span>
                          </span>
                          <Wrench className={`w-5 h-5 ${addNock ? "text-[#1B45C4]" : "text-slate-400"}`} />
                        </button>
                        {addNock && (
                          <div className="border-t-2 border-[#1B45C4]/15 px-4 pb-4 pt-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-600">Antal löpmeter (nock)</span>
                              <span className="rounded-full bg-[#1B45C4]/10 px-3 py-1 text-sm font-bold text-[#1B45C4]" data-testid="calc-addon-nock-meters">{nockMeters[0]} m</span>
                            </div>
                            <Slider value={nockMeters} onValueChange={setNockMeters} min={4} max={60} step={1} className="mt-4" data-testid="calc-addon-nock-slider" />
                            <div className="mt-2 flex justify-between text-xs text-slate-400"><span>4 m</span><span>60 m</span></div>
                          </div>
                        )}
                      </div>

                      {/* Solcellstvätt add-on */}
                      <div className={`mt-3 rounded-xl border-2 transition-all ${addSolar ? "border-[#1B45C4] bg-[#38BDF8]/10" : "border-slate-200"}`}>
                        <button onClick={() => setAddSolar((v) => !v)} data-testid="calc-addon-solar-toggle"
                          className="flex w-full items-center gap-3 p-4 text-left">
                          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${addSolar ? "border-[#1B45C4] bg-[#1B45C4] text-white" : "border-slate-300 text-transparent"}`}>
                            {addSolar ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 text-slate-400" />}
                          </span>
                          <span className="flex-1">
                            <span className={`block text-sm font-bold ${addSolar ? "text-[#1B45C4]" : "text-slate-700"}`}>Solcellstvätt</span>
                            <span className="block text-xs text-slate-500">Rena paneler – från {solarPackages[0].price}/år (abonnemang)</span>
                          </span>
                          <Sun className={`w-5 h-5 ${addSolar ? "text-[#1B45C4]" : "text-slate-400"}`} />
                        </button>
                        {addSolar && (
                          <div className="border-t-2 border-[#1B45C4]/15 px-4 pb-4 pt-3 grid grid-cols-2 gap-2">
                            {solarPackages.map((p, i) => {
                              const active = solarPkg === i;
                              return (
                                <button key={p.name} onClick={() => setSolarPkg(i)} data-testid={`calc-addon-solar-pkg-${i}`}
                                  className={`rounded-lg border-2 p-2.5 text-left transition-all ${active ? "border-[#1B45C4] bg-white" : "border-slate-200 bg-white/60"}`}>
                                  <span className={`block text-xs font-bold ${active ? "text-[#1B45C4]" : "text-slate-700"}`}>{p.price}/år</span>
                                  <span className="block text-[11px] text-slate-500">{p.panels}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* result */}
          <div className="relative hpr-gradient text-white p-7 sm:p-10 flex flex-col">
            <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
            {result.solar ? (
              <>
                  <p className="text-sm font-medium text-white/70">Kampanjpris · {CAMPAIGN_DISCOUNT_PERCENT}% rabatt</p>
                  <p className="mt-2 text-5xl font-extrabold font-display">{fmt(result.price)} kr</p>
                  <p className="mt-1 text-sm text-white/55 line-through">Ord. {fmt(result.originalPrice)} kr</p>
                <div className="mt-6 rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-sm text-white/70">Omfattar</p>
                  <p className="mt-1 text-lg font-bold text-[#38BDF8]">{result.label}</p>
                </div>
                <div className="mt-3 rounded-2xl bg-white/10 p-4 backdrop-blur-sm text-sm text-white/80">
                  <p className="font-bold text-white">Bindningstid 2 år · 2 tvättar/år</p>
                  <p className="mt-1 text-white/70">Du betalar paketpriset för år 1 nu. År 2 faktureras separat inför säsongen – avtalet är bindande i 2 år.</p>
                </div>
                <div className="mt-5 flex items-start gap-2 text-xs text-white/60">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Skonsam tvätt med avjoniserat vatten. Priser inkl. moms.</span>
                </div>
              </>
            ) : result.meter ? (
              <>
                <p className="text-sm font-medium text-white/70">Kampanjpris efter ROT-avdrag</p>
                <p className="mt-2 text-4xl sm:text-5xl font-extrabold font-display">{fmt(result.low)}–{fmt(result.high)} kr</p>
                <p className="mt-1 text-sm text-white/55 line-through">Ord. {fmt(result.originalLow)}–{fmt(result.originalHigh)} kr</p>
                <div className="mt-6 rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
                  <p className="text-sm text-white/70">Pris per löpmeter (efter ROT)</p>
                  <p className="mt-1 text-2xl font-extrabold text-[#38BDF8]">{svc.perLo}–{svc.perHi} kr/m</p>
                </div>
                <div className="mt-5 flex items-start gap-2 text-xs text-white/60">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Priset är vad du betalar efter ROT-avdrag. Exakt pris lämnas efter besiktning.</span>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-white/70">{breakdown && breakdown.hasAddons ? (result.afterRot ? "Samlat kampanjpris efter ROT" : "Samlat kampanjpris") : (result.afterRot ? "Kampanjpris efter ROT-avdrag" : "Beräknat kampanjpris")} · {CAMPAIGN_DISCOUNT_PERCENT}% rabatt</p>
                <p className="mt-2 text-4xl sm:text-5xl font-extrabold font-display" data-testid="calc-total-price">{fmt(breakdown.totalLo)}–{fmt(breakdown.totalHi)} kr</p>
                <p className="mt-1 text-sm text-white/55 line-through">Ord. {fmt(result.originalLow)}–{fmt(result.originalHigh)} kr</p>

                {isKombi && selectedColor && (
                  <div className="mt-4 flex items-center gap-3 rounded-xl bg-white/10 p-3 backdrop-blur-sm" data-testid="calc-selected-color">
                    <span className="h-9 w-9 shrink-0 rounded-lg ring-2 ring-white/30" style={{ backgroundColor: selectedColor.hex }} />
                    <div className="leading-tight">
                      <p className="text-xs text-white/60">Vald takfärg</p>
                      <p className="text-sm font-bold text-white" data-testid="calc-selected-color-label">{selectedColor.label}</p>
                    </div>
                  </div>
                )}

                {breakdown && breakdown.hasAddons ? (
                  <div className="mt-6 rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
                    <p className="text-sm text-white/70">Specifikation</p>
                    <div className="mt-3 space-y-2.5">
                      {breakdown.lines.map((l) => (
                        <div key={l.key} className="flex items-center justify-between gap-3 text-sm" data-testid={`calc-line-${l.key}`}>
                          <span className="text-white/80">{l.label}</span>
                          <span className="font-bold text-[#38BDF8] whitespace-nowrap">
                            {l.yearly ? `${fmt(l.lo)} ${l.unit}` : `${fmt(l.lo)}–${fmt(l.hi)} kr`}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 border-t border-white/15 pt-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-white">Totalt riktpris</span>
                      <span className="text-lg font-extrabold text-white whitespace-nowrap" data-testid="calc-total-sum">{fmt(breakdown.totalLo)}–{fmt(breakdown.totalHi)} kr</span>
                    </div>
                    {breakdown.solarYearly > 0 && (
                      <p className="mt-2 text-xs text-white/60">+ Solcellstvätt {fmt(breakdown.solarYearly)} kr/år (abonnemang, ingår ej i engångssumman).</p>
                    )}
                  </div>
                ) : result.afterRot ? (
                  <div className="mt-6 rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
                    <p className="text-sm text-white/70">Från-pris efter ROT-avdrag (30%)</p>
                    <p className="mt-1 text-2xl font-extrabold text-[#38BDF8]">från {fmt(result.low)} kr</p>
                    <p className="mt-1 text-xs text-white/60">Minst {fmt(svc.min)} kr · ROT-avdraget är redan avdraget.</p>
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
                    <p className="text-sm text-white/70">Ungefärligt pris efter ROT-avdrag</p>
                    <p className="mt-1 text-2xl font-extrabold text-[#38BDF8]">från {fmt(result.rot)} kr</p>
                  </div>
                )}

                <div className="mt-5 flex items-start gap-2 text-xs text-white/60">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{result.afterRot ? `Priset visas efter ROT-avdrag och ${CAMPAIGN_DISCOUNT_PERCENT}% kampanjrabatt. Exakt pris lämnas efter kostnadsfri besiktning.` : `Riktpriset inkluderar ${CAMPAIGN_DISCOUNT_PERCENT}% kampanjrabatt. Exakt pris lämnas efter kostnadsfri besiktning.`}</span>
                </div>
              </>
            )}
            <div className="mt-6 rounded-2xl bg-white/10 p-4 backdrop-blur-sm" data-testid="calc-takkoll-nudge">
              <p className="text-sm text-white/85">Det här är en uppskattning. För ett exakt pris gör vi en kostnadsfri kontroll av taket.</p>
              <a href="#takkoll" className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#38BDF8] hover:underline">
                Boka kostnadsfri TakKoll <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            {result.solar ? (
              <>
                <Button asChild className="mt-auto w-full rounded-full h-13 py-3 bg-[#38BDF8] text-[#062a4f] hover:bg-[#7dd3fc] font-bold text-base">
                  <a href="#kontakt" data-testid="calc-cta-btn">Få exakt offert <ArrowRight className="ml-2 w-5 h-5" /></a>
                </Button>
                <SolarCheckout
                  pkgIndex={pkg}
                  referrer={referrer}
                  triggerLabel="Boka & betala nu"
                  triggerClassName="mt-3 w-full rounded-full h-13 py-3 bg-white text-[#1B45C4] hover:bg-slate-100 font-bold text-base"
                />
              </>
            ) : (
              <div className="mt-auto">
                <TaktvattBooking
                  {...bookingProps}
                  campaignDiscount
                  triggerLabel={`Boka ${svc.label}`}
                  triggerClassName="w-full rounded-full h-13 py-3 bg-[#38BDF8] text-[#062a4f] hover:bg-[#7dd3fc] font-bold text-base"
                />
                <a href="#kontakt" data-testid="calc-cta-btn" className="mt-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white">
                  Eller få en exakt offert <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
